import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_INVENTORY_WAREHOUSES = 'FETCH_INVENTORY_WAREHOUSES';
export const FETCH_INVENTORY_WAREHOUSES_SUCCESS = 'FETCH_INVENTORY_WAREHOUSES_SUCCESS';
export const FETCH_INVENTORY_WAREHOUSES_FAIL = 'FETCH_INVENTORY_WAREHOUSES_FAIL';
export const CLEAR_INVENTORY_WAREHOUSES = 'CLEAR_INVENTORY_WAREHOUSES';
export const CREATE_INVENTORY_WAREHOUSE = 'CREATE_INVENTORY_WAREHOUSE';
export const CREATE_INVENTORY_WAREHOUSE_SUCCESS = 'CREATE_INVENTORY_WAREHOUSE_SUCCESS';
export const CREATE_INVENTORY_WAREHOUSE_FAIL = 'CREATE_INVENTORY_WAREHOUSE_FAIL';
export const UPDATE_INVENTORY_WAREHOUSE = 'UPDATE_INVENTORY_WAREHOUSE';
export const UPDATE_INVENTORY_WAREHOUSE_SUCCESS = 'UPDATE_INVENTORY_WAREHOUSE_SUCCESS';
export const UPDATE_INVENTORY_WAREHOUSE_FAIL = 'UPDATE_INVENTORY_WAREHOUSE_FAIL';
export const DELETE_INVENTORY_WAREHOUSE = 'DELETE_INVENTORY_WAREHOUSE';
export const DELETE_INVENTORY_WAREHOUSE_SUCCESS = 'DELETE_INVENTORY_WAREHOUSE_SUCCESS';
export const DELETE_INVENTORY_WAREHOUSE_FAIL = 'DELETE_INVENTORY_WAREHOUSE_FAIL';

// Reducer
export default function inventoryWarehousesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_INVENTORY_WAREHOUSES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INVENTORY_WAREHOUSES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORY_WAREHOUSES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_INVENTORY_WAREHOUSES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_INVENTORY_WAREHOUSE_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_INVENTORY_WAREHOUSE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_INVENTORY_WAREHOUSE_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchInventoryWarehouses(payload) {
  return { type: FETCH_INVENTORY_WAREHOUSES, payload };
}

export function fetchInventoryWarehousesSuccess(payload) {
  return { type: FETCH_INVENTORY_WAREHOUSES_SUCCESS, payload };
}

export function fetchInventoryWarehousesFail(payload) {
  return { type: FETCH_INVENTORY_WAREHOUSES_FAIL, payload };
}

export function clearInventoryWarehouses(payload) {
  return { type: CLEAR_INVENTORY_WAREHOUSES, payload };
}

export function createInventoryWarehouse(payload) {
  return { type: CREATE_INVENTORY_WAREHOUSE, payload };
}

export function createInventoryWarehouseSuccess(payload) {
  return { type: CREATE_INVENTORY_WAREHOUSE_SUCCESS, payload };
}

export function createInventoryWarehouseFail(payload) {
  return { type: CREATE_INVENTORY_WAREHOUSE_FAIL, payload };
}

export function updateInventoryWarehouse(payload) {
  return { type: UPDATE_INVENTORY_WAREHOUSE, payload };
}

export function updateInventoryWarehouseSuccess(payload) {
  return { type: UPDATE_INVENTORY_WAREHOUSE_SUCCESS, payload };
}

export function updateInventoryWarehouseFail(payload) {
  return { type: UPDATE_INVENTORY_WAREHOUSE_FAIL, payload };
}

export function deleteInventoryWarehouse(payload) {
  return { type: DELETE_INVENTORY_WAREHOUSE, payload };
}

export function deleteInventoryWarehouseSuccess(payload) {
  return { type: DELETE_INVENTORY_WAREHOUSE_SUCCESS, payload };
}

export function deleteInventoryWarehouseFail(payload) {
  return { type: DELETE_INVENTORY_WAREHOUSE_FAIL, payload };
}

// Requests

let abort;

export function requestFetchInventoryWarehouses(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`inventory/warehouse/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('inventory/warehouse/');
  abort = request.abort;
  return request.res;
}

export function requestSearchInventoryWarehouses({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('inventory/warehouse/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Location',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateInventoryWarehouse(payload) {
  return callApi('inventory/warehouse/', 'POST', payload);
}

export function requestUpdateInventoryWarehouse(payload) {
  return callApi('inventory/warehouse/', 'PATCH', payload);
}

export function requestDeleteInventoryWarehouse(payload) {
  return callApi(`inventory/warehouse/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchInventoryWarehousesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchInventoryWarehouses, action.payload)
      : yield call(requestFetchInventoryWarehouses, action.payload);
    yield put(fetchInventoryWarehousesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchInventoryWarehousesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createInventoryWarehouseSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateInventoryWarehouse, values);
    yield put(createInventoryWarehouseSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Inventory Warehouse Location successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createInventoryWarehouseFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInventoryWarehouseSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateInventoryWarehouse, values);
    yield put(updateInventoryWarehouseSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Warehouse Location successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateInventoryWarehouseFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInventoryWarehouseSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteInventoryWarehouse, ID);
    yield put(deleteInventoryWarehouseSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Warehouse Location successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteInventoryWarehouseFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Inventory Warehouse Location',
      variant: 'warning',
    }));
  }
}

export function* inventoryWarehousesSaga() {
  yield takeLatest(FETCH_INVENTORY_WAREHOUSES, fetchInventoryWarehousesSaga);
  yield takeEvery(CREATE_INVENTORY_WAREHOUSE, createInventoryWarehouseSaga);
  yield takeEvery(UPDATE_INVENTORY_WAREHOUSE, updateInventoryWarehouseSaga);
  yield takeEvery(DELETE_INVENTORY_WAREHOUSE, deleteInventoryWarehouseSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/inventoryWarehouses.js